import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataSource } from "@firecms/core";

import {
  buildAssignedParticipantsCollection,
  buildAssignedParticipantsPath,
} from "../collections/participants";
import {
  buildEventsPath,
  eventsCollection,
  type Event,
} from "../collections/events";
import {
  buildContentInstallsCollection,
  buildContentInstallsPath,
  type ContentInstall,
} from "../collections/contentInstalls";
import { participantBaseLabel } from "../firebase/fetchUserDisplayNames";
import type {
  ContentInstallRow,
  ContentInstallStatus,
  ContentInstallSummary,
} from "../types/contentInstalls";
import type { EventCollectionId } from "../workspace/collectionIds";

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (
    value != null &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function versionFromRef(ref: unknown): number | null {
  if (ref == null || typeof ref !== "object") return null;
  const id =
    "id" in ref && typeof (ref as { id?: unknown }).id === "string"
      ? (ref as { id: string }).id
      : null;
  if (!id) return null;
  const parsed = Number.parseInt(id, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveStatus(
  installedVersion: number | null,
  publishedVersion: number | null
): ContentInstallStatus {
  if (installedVersion == null) return "not_installed";
  if (publishedVersion == null) return "outdated";
  if (installedVersion === publishedVersion) return "up_to_date";
  return "outdated";
}

export function useContentInstalls(eventId: EventCollectionId | null) {
  const dataSource = useDataSource();
  const [rows, setRows] = useState<ContentInstallRow[]>([]);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    try {
      const eventsPath = buildEventsPath({ accountId: eventId.accountId });
      const [eventEntity, participants, installs] = await Promise.all([
        dataSource.fetchEntity<Event>({
          path: eventsPath,
          collection: eventsCollection,
          entityId: eventId.eventId,
        }),
        dataSource.fetchCollection({
          path: buildAssignedParticipantsPath(eventId),
          collection: buildAssignedParticipantsCollection(eventId),
        }),
        dataSource.fetchCollection<ContentInstall>({
          path: buildContentInstallsPath(eventId),
          collection: buildContentInstallsCollection(eventId),
        }),
      ]);

      const published = versionFromRef(eventEntity?.values?.contentReleaseRef);
      setPublishedVersion(published);

      const installByUid = new Map<
        string,
        { version: number; at: Date | null }
      >();
      for (const install of installs) {
        const version = install.values?.installedContentVersion;
        if (typeof version !== "number") continue;
        installByUid.set(install.id, {
          version,
          at: toDate(install.values?.installedContentAt),
        });
      }

      const nextRows: ContentInstallRow[] = participants.map((participant) => {
        const uid = participant.id;
        const install = installByUid.get(uid);
        const installedContentVersion = install?.version ?? null;
        return {
          uid,
          label: participantBaseLabel(
            uid,
            participant.values?.participantNumber
          ),
          installedContentVersion,
          installedContentAt: install?.at ?? null,
          status: resolveStatus(installedContentVersion, published),
        };
      });

      nextRows.sort((a, b) => a.label.localeCompare(b.label));
      setRows(nextRows);
    } finally {
      setLoading(false);
    }
  }, [dataSource, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary: ContentInstallSummary = useMemo(() => {
    let upToDate = 0;
    let outdated = 0;
    let notInstalled = 0;
    for (const row of rows) {
      if (row.status === "up_to_date") upToDate++;
      else if (row.status === "outdated") outdated++;
      else notInstalled++;
    }
    return {
      publishedVersion,
      totalParticipants: rows.length,
      upToDate,
      outdated,
      notInstalled,
    };
  }, [rows, publishedVersion]);

  return { rows, summary, loading, reload: load };
}
